/**
 * History Detail Modal
 * 
 * 평가 히스토리 상세 보기 모달
 * ResultBoard 재활용 + 다시 평가하기 기능
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLanguage, formatDate, parseUTCDate } from '../i18n';
import { useBodyScrollLock } from '../hooks';
import { getHistoryDetail, downloadHistoryPdf, downloadTestcaseFile, downloadPlanningFile, getQuotaInfo, type QuotaInfo, type HistoryDetailResponse } from '../api';
import type { ReleaseInput, StructuredRecommendation, RecommendationPriority, SuggestedTestCase, TestCaseStatus } from '../types';
import { useAuth } from '../auth';
import MaxAIReport from './MaxAIReport';
import GitHubReportBlock, { type GithubContextForReport } from './GitHubReportBlock';
import TriRadarChart from './TriRadarChart';
import { stripBullet } from '../utils';

interface HistoryDetailModalProps {
  isOpen: boolean;
  recordId: number | null;
  onClose: () => void;
  onReEvaluate: (input: ReleaseInput) => void;
  onOpenPricing?: () => void;
}

// AI 제안 테스트 케이스 결과 컴포넌트 (ResultBoard.tsx와 동일)
function SuggestedTestcasesResult({ testcases, lang }: { testcases: SuggestedTestCase[]; lang: 'ko' | 'en' }) {
  const statusConfig: Record<TestCaseStatus, { icon: string; color: string; label: string }> = {
    'pass': { icon: '✓', color: 'text-status-go', label: lang === 'ko' ? '통과' : 'Pass' },
    'fail': { icon: '✗', color: 'text-status-danger', label: lang === 'ko' ? '실패' : 'Fail' },
    'n/a': { icon: '−', color: 'text-terminal-muted', label: lang === 'ko' ? '해당없음' : 'N/A' },
    'n/t': { icon: '○', color: 'text-status-warning', label: lang === 'ko' ? '미테스트' : 'Not Tested' },
  };

  const priorityConfig: Record<string, { color: string; label: string }> = {
    'high': { color: 'text-status-danger', label: lang === 'ko' ? '높음' : 'High' },
    'medium': { color: 'text-status-warning', label: lang === 'ko' ? '보통' : 'Medium' },
    'low': { color: 'text-terminal-muted', label: lang === 'ko' ? '낮음' : 'Low' },
  };

  const passCount = testcases.filter(tc => tc.status === 'pass').length;
  const failCount = testcases.filter(tc => tc.status === 'fail').length;
  const ntCount = testcases.filter(tc => tc.status === 'n/t').length;
  const naCount = testcases.filter(tc => tc.status === 'n/a').length;

  return (
    <div className="card p-4 mt-4">
      <div className="text-terminal-muted text-xs mb-3 uppercase tracking-wide flex items-center gap-2">
        <span>💡</span>
        <span># {lang === 'ko' ? 'AI 제안 테스트 케이스' : 'AI Suggested Test Cases'}</span>
      </div>
      
      {/* 요약 */}
      <div className="flex flex-wrap gap-3 mb-3 text-xs">
        <span className="text-status-go">✓ {passCount} {lang === 'ko' ? '통과' : 'Pass'}</span>
        <span className="text-status-danger">✗ {failCount} {lang === 'ko' ? '실패' : 'Fail'}</span>
        <span className="text-status-warning">○ {ntCount} {lang === 'ko' ? '미테스트' : 'Not Tested'}</span>
        <span className="text-terminal-muted">− {naCount} {lang === 'ko' ? '해당없음' : 'N/A'}</span>
      </div>

      {/* 테스트 케이스 목록 */}
      <div className="space-y-2">
        {testcases.map((tc) => {
          const status = statusConfig[tc.status];
          const priority = priorityConfig[tc.priority] || priorityConfig['medium'];
          
          return (
            <div key={tc.id} className="bg-terminal-dim/20 p-2 rounded border border-terminal-border">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-xs mb-1">
                    <span className="text-terminal-muted font-mono">{tc.id}</span>
                    <span className="text-terminal-dim">|</span>
                    <span className="text-terminal-muted">{tc.category}</span>
                    <span className="text-terminal-dim">|</span>
                    <span className={priority.color}>{priority.label}</span>
                  </div>
                  <p className="text-sm text-terminal-text">{tc.title || tc.description}</p>
                  {tc.title && tc.description && tc.title !== tc.description && (
                    <p className="text-xs text-terminal-muted mt-1">{tc.description}</p>
                  )}
                  
                  {/* GitHub 커밋 정보 표시 */}
                  {tc.github_commit_sha && (
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-[10px]">
                      <span className="px-1.5 py-0.5 bg-purple-500/10 border border-purple-500/30 rounded text-purple-400 font-mono">
                        🔗 {lang === 'ko' ? '커밋' : 'Commit'}: {tc.github_commit_sha.substring(0, 7)}
                      </span>
                      {tc.github_repo && (
                        <span className="px-1.5 py-0.5 bg-blue-500/10 border border-blue-500/30 rounded text-blue-400">
                          📦 {tc.github_repo}
                        </span>
                      )}
                    </div>
                  )}
                  
                  {/* 담당자 정보 표시 */}
                  {(tc.author || tc.tester || tc.modifier) && (
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-[10px]">
                      {tc.author && (
                        <span className="px-1.5 py-0.5 bg-cyan-500/10 border border-cyan-500/30 rounded text-cyan-400">
                          👤 {lang === 'ko' ? '작성자' : 'Author'}: {tc.author}
                        </span>
                      )}
                      {tc.tester && (
                        <span className="px-1.5 py-0.5 bg-green-500/10 border border-green-500/30 rounded text-green-400">
                          🧪 {lang === 'ko' ? '진행자' : 'Tester'}: {tc.tester}
                        </span>
                      )}
                      {tc.modifier && (
                        <span className="px-1.5 py-0.5 bg-orange-500/10 border border-orange-500/30 rounded text-orange-400">
                          ✏️ {lang === 'ko' ? '수정자' : 'Modifier'}: {tc.modifier}
                        </span>
                      )}
                    </div>
                  )}
                  
                  {/* 메모 표시 */}
                  {tc.memo && (
                    <div className="mt-2 p-2 bg-terminal-bg rounded border border-terminal-border">
                      <div className="flex items-center gap-1 text-xs text-terminal-dim mb-1">
                        <span>📝</span>
                        <span>{lang === 'ko' ? '메모' : 'Memo'}</span>
                      </div>
                      <p className="text-xs text-terminal-muted whitespace-pre-wrap">{tc.memo}</p>
                    </div>
                  )}
                </div>
                <div className={`flex items-center gap-1 px-2 py-1 rounded ${status.color} bg-terminal-dim/30 text-xs whitespace-nowrap`}>
                  <span>{status.icon}</span>
                  <span>{status.label}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function HistoryDetailModal({ isOpen, recordId, onClose, onReEvaluate, onOpenPricing }: HistoryDetailModalProps) {
  const { lang, t } = useLanguage();
  const { user } = useAuth();
  const [detail, setDetail] = useState<HistoryDetailResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quotaInfo, setQuotaInfo] = useState<QuotaInfo | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [showMaxReport, setShowMaxReport] = useState(false);
  const [isDownloadingTestcase, setIsDownloadingTestcase] = useState(false);
  const [downloadingPlanningIndex, setDownloadingPlanningIndex] = useState<number | null>(null);
  const [showPlanningFilesModal, setShowPlanningFilesModal] = useState(false);
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [planningImages, setPlanningImages] = useState<{ url: string; name: string }[]>([]);
  const [isLoadingImages, setIsLoadingImages] = useState(false);

  // 이미지 파일 확인 함수
  const isImageFile = (filename: string) => {
    const ext = filename.toLowerCase().split('.').pop();
    return ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'].includes(ext || '');
  };

  // 기획서 이미지 로드
  const loadPlanningImages = useCallback(async (fileNames: string[], recordIdParam: number) => {
    const imageFiles = fileNames
      .map((name, index) => ({ name, index }))
      .filter(({ name }) => isImageFile(name));
    
    if (imageFiles.length === 0) {
      setPlanningImages([]);
      return;
    }
    
    setIsLoadingImages(true);
    try {
      const images: { url: string; name: string }[] = [];
      
      for (const { name, index } of imageFiles) {
        try {
          const { blob } = await downloadPlanningFile(recordIdParam, index);
          const url = URL.createObjectURL(blob);
          images.push({ url, name });
        } catch (e) {
          console.error(`Failed to load image ${name}:`, e);
        }
      }
      
      setPlanningImages(images);
    } catch (e) {
      console.error('Failed to load planning images:', e);
    } finally {
      setIsLoadingImages(false);
    }
  }, []);

  // 상세 데이터 로드
  const loadDetail = useCallback(async () => {
    if (!recordId) return;
    
    setIsLoading(true);
    setError(null);
    try {
      const response = await getHistoryDetail(recordId);
      setDetail(response);
      
      // 기획서 이미지 로드
      if (response.has_planning_files && response.planning_file_names.length > 0) {
        loadPlanningImages(response.planning_file_names, recordId);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t.history.loadError);
    } finally {
      setIsLoading(false);
    }
  }, [recordId, t.history.loadError, loadPlanningImages]);

  // 모달 열릴 때 데이터 로드
  useEffect(() => {
    if (isOpen && recordId) {
      loadDetail();
    } else if (!isOpen) {
      setDetail(null);
      setError(null);
      setPdfError(null);
      // 이미지 URL 해제
      planningImages.forEach(img => URL.revokeObjectURL(img.url));
      setPlanningImages([]);
    }
  }, [isOpen, recordId, loadDetail]); // eslint-disable-line react-hooks/exhaustive-deps

  // Quota 정보 가져오기
  useEffect(() => {
    if (user && isOpen) {
      getQuotaInfo()
        .then(setQuotaInfo)
        .catch(console.error);
    }
  }, [user, isOpen]);

  const canDownloadPdf = quotaInfo?.limits.pdf_download ?? false;
  
  // MAX/Team 플랜 여부 확인 (첨부 기획 이미지, AI 리포트 사용 가능)
  const isCurrentlyMaxOrTeam = quotaInfo?.plan === 'max' || quotaInfo?.actual_plan === 'max' || quotaInfo?.plan === 'team' || quotaInfo?.team !== null;

  // PDF 뷰어 열기 핸들러 (모든 플랜에서 사용 가능)
  const handleOpenPdfViewer = async () => {
    if (!recordId || typeof recordId !== 'number' || isPdfLoading) {
      console.error('Invalid recordId:', recordId);
      return;
    }
    
    setIsPdfLoading(true);
    setPdfError(null);
    
    try {
      console.log('Loading PDF for viewer, recordId:', recordId);
      const blob = await downloadHistoryPdf(recordId, lang);
      
      // 기존 URL 해제
      if (pdfBlobUrl) {
        window.URL.revokeObjectURL(pdfBlobUrl);
      }
      
      // Blob URL 생성
      const url = window.URL.createObjectURL(blob);
      setPdfBlobUrl(url);
      setShowPdfViewer(true);
    } catch (err) {
      console.error('PDF 로딩 오류:', err);
      setPdfError(lang === 'ko' ? 'PDF 로딩 중 오류가 발생했습니다.' : 'Error loading PDF.');
    } finally {
      setIsPdfLoading(false);
    }
  };

  // PDF 다운로드 핸들러 (Pro 이상 플랜에서만)
  const handleDownloadPdf = () => {
    if (!canDownloadPdf) {
      setPdfError(t.result.pdfUpgradeHint);
      return;
    }
    
    if (!pdfBlobUrl) return;
    
    // 파일명 생성
    const sanitizedName = (detail?.feature_name || 'report').replace(/[^a-zA-Z0-9가-힣_\- ]/g, '').replace(/\s+/g, '_');
    const status = detail?.status || 'go';
    const statusSuffixMap: Record<string, string> = {
      go: 'GO',
      conditional: 'Conditional',
      holding: 'Holding',
      no_go: 'NoGo',
    };
    const statusSuffix = statusSuffixMap[status] || status;
    const fileName = `${sanitizedName}-${statusSuffix}.pdf`;
    
    // Blob URL로 다운로드
    const link = document.createElement('a');
    link.href = pdfBlobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // PDF 뷰어 닫기
  const handleClosePdfViewer = () => {
    setShowPdfViewer(false);
    if (pdfBlobUrl) {
      window.URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
  };

  // 다시 평가하기 핸들러 - 버전 번호 자동 증가
  const handleReEvaluate = () => {
    if (detail) {
      // 기존 제목에서 버전 번호 추출 및 증가
      let newFeatureName = detail.feature_name;
      const versionMatch = newFeatureName.match(/\s*ver\s*(\d+)\s*$/i);
      
      if (versionMatch) {
        // 기존 버전 번호 증가
        const currentVersion = parseInt(versionMatch[1], 10);
        newFeatureName = newFeatureName.replace(/\s*ver\s*\d+\s*$/i, ` ver ${currentVersion + 1}`);
      } else {
        // 버전 번호가 없으면 ver 2 추가
        newFeatureName = `${newFeatureName} ver 2`;
      }
      
      // 백엔드 응답에서 input_data 형식으로 재조합
      const inputData: ReleaseInput = {
        feature_name: newFeatureName,
        release_date: detail.release_date,
        planning_intent: detail.planning_intent || [],
        risks: detail.risks || [],
        qa_status: detail.qa_status || {},
        suggested_testcases: detail.suggested_testcases || [],
        github_context: (detail.github_context as unknown as Record<string, unknown> | null) ?? null,
        lang: lang,
        is_re_evaluate: true,  // 다시 평가 플래그
      };
      onReEvaluate(inputData);
      onClose();
    }
  };

  // 테스트케이스 파일 다운로드 핸들러
  const handleDownloadTestcase = async () => {
    if (!recordId || isDownloadingTestcase) return;
    
    setIsDownloadingTestcase(true);
    try {
      const { blob, filename } = await downloadTestcaseFile(recordId);
      
      // 파일 다운로드
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Failed to download testcase file:', err);
    } finally {
      setIsDownloadingTestcase(false);
    }
  };

  // 기획 파일 다운로드 핸들러
  const handleDownloadPlanningFile = async (fileIndex: number) => {
    if (!recordId || downloadingPlanningIndex !== null) return;
    
    setDownloadingPlanningIndex(fileIndex);
    try {
      const { blob, filename } = await downloadPlanningFile(recordId, fileIndex);
      
      // 파일 다운로드
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Failed to download planning file:', err);
    } finally {
      setDownloadingPlanningIndex(null);
    }
  };

  // 모달 열릴 때 배경 스크롤 방지
  useBodyScrollLock(isOpen);

  const result = detail?.result;

  // 삼각 그래프용 점수 계산 (breakdown에서 추출) - Hook은 조건부 return 이전에 호출해야 함
  const radarScores = useMemo(() => {
    if (!result?.breakdown) return { planning: 70, risk: 65, test: 75 };
    
    const planningItem = result.breakdown.find(b => 
      b.category.toLowerCase().includes('planning') || b.category_ko?.includes('기획')
    );
    const riskItem = result.breakdown.find(b => 
      b.category.toLowerCase().includes('risk') || b.category_ko?.includes('리스크')
    );
    const testItem = result.breakdown.find(b => 
      b.category.toLowerCase().includes('test') || b.category.toLowerCase().includes('qa') ||
      b.category_ko?.includes('테스트') || b.category_ko?.includes('QA')
    );

    return {
      planning: planningItem ? Math.round((planningItem.score / planningItem.max_score) * 100) : 70,
      risk: riskItem ? Math.round((riskItem.score / riskItem.max_score) * 100) : 65,
      test: testItem ? Math.round((testItem.score / testItem.max_score) * 100) : 75,
    };
  }, [result?.breakdown]);

  if (!isOpen) return null;
  
  // inputData 재구성 (MAX AI 리포트용)
  const inputDataForReport: ReleaseInput | null = detail ? {
    feature_name: detail.feature_name,
    release_date: detail.release_date,
    planning_intent: detail.planning_intent || [],
    risks: detail.risks || [],
    qa_status: detail.qa_status || {},
    suggested_testcases: detail.suggested_testcases || [],
    lang: lang,
  } : null;

  // 상태 설정
  const statusConfig = {
    go: { label: t.status.go, badge: 'badge-go', color: 'text-status-go', desc: t.status.goDesc },
    conditional: { label: t.status.conditional, badge: 'badge-conditional', color: 'text-status-warning', desc: t.status.conditionalDesc },
    holding: { label: t.status.holding, badge: 'badge-holding', color: 'text-status-holding', desc: t.status.holdingDesc },
    no_go: { label: t.status.noGo, badge: 'badge-nogo', color: 'text-status-danger', desc: t.status.noGoDesc },
  };

  const getBarColor = (score: number, max: number) => {
    const pct = (score / max) * 100;
    if (pct >= 70) return 'bg-status-go';
    if (pct >= 40) return 'bg-status-warning';
    return 'bg-status-danger';
  };

  const getScoreGrade = (score: number) => {
    if (score >= 80) return { grade: 'A', desc: t.grade.excellent };
    if (score >= 70) return { grade: 'B', desc: t.grade.good };
    if (score >= 60) return { grade: 'C', desc: t.grade.average };
    if (score >= 50) return { grade: 'D', desc: t.grade.poor };
    return { grade: 'F', desc: t.grade.insufficient };
  };

  // 우선순위 설정
  const getPriorityConfig = (priority: RecommendationPriority) => {
    const configs = {
      high: { 
        color: 'text-status-danger', 
        bg: 'bg-status-danger/10 border-status-danger/30',
        label: t.result.priorityHigh,
        icon: '🔴'
      },
      medium: { 
        color: 'text-status-warning', 
        bg: 'bg-status-warning/10 border-status-warning/30',
        label: t.result.priorityMedium,
        icon: '🟡'
      },
      low: { 
        color: 'text-status-go', 
        bg: 'bg-status-go/10 border-status-go/30',
        label: t.result.priorityLow,
        icon: '🟢'
      },
    };
    return configs[priority];
  };

  const config = result ? statusConfig[result.status] : null;
  const scoreGrade = result ? getScoreGrade(result.confidence_score) : null;
  const createdDate = detail ? formatDate(parseUTCDate(detail.created_at), lang) : '';

  return (
    <React.Fragment>
      {/* MAX AI 리포트 모달 */}
      {result && inputDataForReport && (
        <MaxAIReport
          result={result}
          inputData={inputDataForReport}
          isOpen={showMaxReport}
          onClose={() => setShowMaxReport(false)}
          createdAt={detail?.created_at}
          createdWithPlan={detail?.created_with_plan || undefined}
        />
      )}

      {/* PDF 뷰어 모달 */}
      {showPdfViewer && pdfBlobUrl && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={handleClosePdfViewer}
        >
          <div
            className="bg-terminal-bg border border-terminal-border rounded-xl shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* 헤더 */}
            <div className="flex-shrink-0 border-b border-terminal-border px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-lg">📄</span>
                <h3 className="text-base font-semibold text-terminal-text">
                  {lang === 'ko' ? 'PDF 리포트' : 'PDF Report'}
                </h3>
                {/* 워터마크 안내 (Free/Pro) */}
                {quotaInfo && (quotaInfo.plan === 'free' || quotaInfo.plan === 'pro') && (
                  <span className="text-xs text-terminal-muted bg-terminal-dim/30 px-2 py-1 rounded">
                    {lang === 'ko' ? '워터마크 포함' : 'Includes watermark'}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {/* 다운로드 버튼 (Pro 이상) */}
                {canDownloadPdf && (
                  <button
                    onClick={handleDownloadPdf}
                    className="px-4 py-2 bg-status-go text-white rounded-lg hover:bg-status-go/90 transition-colors flex items-center gap-2 text-sm font-medium"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    {lang === 'ko' ? '다운로드' : 'Download'}
                  </button>
                )}
                {/* 닫기 버튼 */}
                <button
                  onClick={handleClosePdfViewer}
                  className="p-2 hover:bg-terminal-dim/30 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5 text-terminal-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* PDF 뷰어 */}
            <div className="flex-1 overflow-hidden p-4">
              <iframe
                src={pdfBlobUrl}
                className="w-full h-full rounded-lg border border-terminal-border"
                title="PDF Report"
              />
            </div>

            {/* 푸터 - Free 플랜 업그레이드 안내 */}
            {!canDownloadPdf && (
              <div className="flex-shrink-0 border-t border-terminal-border px-5 py-3 bg-terminal-dim/20">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-terminal-muted">
                    {lang === 'ko' 
                      ? '⭐ Pro 이상 플랜에서 PDF 다운로드가 가능합니다' 
                      : '⭐ PDF download available on Pro plan or higher'}
                  </p>
                  <button
                    onClick={() => {
                      handleClosePdfViewer();
                      onOpenPricing?.();
                    }}
                    className="px-4 py-1.5 text-sm bg-status-go text-white rounded-lg hover:bg-status-go/90 transition-colors"
                  >
                    {lang === 'ko' ? '업그레이드' : 'Upgrade'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 기획서 파일 리스트 모달 */}
      {showPlanningFilesModal && detail?.planning_file_names && detail.planning_file_names.length > 0 && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => setShowPlanningFilesModal(false)}
        >
          <div
            className="bg-terminal-bg border border-terminal-border rounded-xl shadow-2xl w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            {/* 헤더 */}
            <div className="border-b border-terminal-border px-5 py-4 flex items-center justify-between">
              <h3 className="text-base font-semibold text-terminal-text">
                {lang === 'ko' ? '첨부한 기획서' : 'Attached Planning Documents'}
              </h3>
              <button
                onClick={() => setShowPlanningFilesModal(false)}
                className="p-1.5 hover:bg-terminal-dim/30 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-terminal-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* 파일 리스트 */}
            <div className="px-5 py-4">
              {/* 테이블 헤더 */}
              <div className="flex items-center justify-between text-xs text-terminal-muted uppercase tracking-wide mb-3 px-1">
                <span>{lang === 'ko' ? '파일명' : 'Filename'}</span>
                <span>{lang === 'ko' ? '다운로드' : 'Download'}</span>
              </div>
              <div className="border-t border-terminal-border mb-3"></div>
              
              {/* 파일 목록 */}
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {detail.planning_file_names.map((filename, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between py-2 px-3 bg-terminal-dim/20 rounded-lg hover:bg-terminal-dim/40 transition-colors"
                  >
                    <span className="text-sm text-terminal-text truncate flex-1 mr-4" title={filename}>
                      {filename}
                    </span>
                    <button
                      onClick={() => {
                        handleDownloadPlanningFile(index);
                      }}
                      disabled={downloadingPlanningIndex !== null}
                      className="p-2 text-status-go hover:bg-status-go/10 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {downloadingPlanningIndex === index ? (
                        <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
        onClick={onClose}
      >
      <div
        className="bg-terminal-bg border border-terminal-border rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div className="flex-shrink-0 border-b border-terminal-border px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="p-1 hover:bg-terminal-dim/30 rounded transition-colors"
            >
              <svg className="w-5 h-5 text-terminal-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-bold text-terminal-text">
                  {detail?.feature_name || t.history.detail}
                </h2>
                {/* 플랜 배지 */}
                {detail?.created_with_plan && (
                  <span className={`px-2 py-0.5 text-xs font-semibold rounded ${
                    detail.created_with_plan === 'team' 
                      ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                      : detail.created_with_plan === 'max' 
                      ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' 
                      : detail.created_with_plan === 'pro'
                      ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                      : 'bg-terminal-dim/50 text-terminal-muted border border-terminal-border'
                  }`}>
                    {detail.created_with_plan.toUpperCase()}
                  </span>
                )}
              </div>
              <p className="text-sm text-terminal-muted">
                {createdDate}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-terminal-dim/30 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5 text-terminal-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* 본문 */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* 로딩 */}
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-3 border-status-go/30 border-t-status-go rounded-full animate-spin"></div>
            </div>
          )}

          {/* 에러 */}
          {error && !isLoading && (
            <div className="p-4 bg-status-danger/10 border border-status-danger/30 rounded-lg text-status-danger text-sm text-center">
              {error}
              <button
                onClick={loadDetail}
                className="block mx-auto mt-2 text-xs underline hover:no-underline"
              >
                {t.history.retry}
              </button>
            </div>
          )}

          {/* 결과 표시 (ResultBoard 스타일) */}
          {!isLoading && !error && result && config && scoreGrade && (
            <div className="space-y-4">
              {/* 리포트 헤더 */}
              <div className="card p-4">
                <div className="flex items-center justify-between text-terminal-dim text-xs mb-4">
                  <span>{t.result.reportTitle}</span>
                  <span>{createdDate}</span>
                </div>
                
                <div className="text-terminal-dim text-xs mb-2">
                  $ evaluate --feature "{result.feature_name}"
                </div>
                
                <div className="space-y-4">
                  <div>
                    <div className="text-terminal-muted text-xs mb-1">{t.result.feature}</div>
                    <div className="text-terminal-text text-lg font-semibold">{result.feature_name}</div>
                  </div>
                  
                  <div>
                    <div className="text-terminal-muted text-xs mb-1">{t.result.releaseDate}</div>
                    <div className="text-terminal-text">{result.release_date}</div>
                  </div>
                </div>
              </div>

              {/* 점수 및 판정 */}
              <div className="card p-4">
                <div className="text-terminal-muted text-xs mb-3 uppercase tracking-wide">
                  # {t.result.evaluationResult}
                </div>
                
                <div className="space-y-4">
                  {/* 점수 */}
                  <div className="flex items-end gap-4">
                    <div>
                      <div className="text-terminal-muted text-xs mb-1">{t.result.confidenceScore}</div>
                      <div className={`text-5xl font-bold ${config.color}`}>
                        {result.confidence_score}
                        <span className="text-terminal-dim text-2xl">/100</span>
                      </div>
                    </div>
                    <div className="pb-2">
                      <span className={`text-2xl font-bold ${config.color}`}>
                        {scoreGrade.grade}
                      </span>
                      <span className="text-terminal-muted text-sm ml-2">
                        ({scoreGrade.desc})
                      </span>
                    </div>
                  </div>

                  {/* 상태 */}
                  <div className="pt-4 border-t border-terminal-border">
                    <div className="flex items-center gap-3">
                      <span className="text-terminal-muted text-sm">{t.result.status}:</span>
                      <span className={`px-3 py-1 rounded text-sm font-bold ${config.badge}`}>
                        {config.label}
                      </span>
                      <span className={`text-sm ${config.color}`}>{config.desc}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* TRI 분석 (삼각형 레이더 차트) - MAX/Team 플랜 전용 */}
              {isCurrentlyMaxOrTeam && (detail?.created_with_plan === 'max' || detail?.created_with_plan === 'team') && (
                <div className="card p-4 border-purple-500/30 bg-gradient-to-br from-purple-500/5 to-transparent">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-terminal-muted text-xs uppercase tracking-wide">
                      # {lang === 'ko' ? 'TRI 분석' : 'TRI Analysis'}
                      <span className="text-terminal-dim normal-case ml-2">
                        ({lang === 'ko' ? '기획-리스크-테스트 균형' : 'Planning-Risk-Test Balance'})
                      </span>
                    </div>
                    <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs font-semibold rounded border border-purple-500/30">
                      {detail.created_with_plan.toUpperCase()}
                    </span>
                  </div>
                  
                  <div className="flex justify-center">
                    <TriRadarChart
                      planning={radarScores.planning}
                      risk={radarScores.risk}
                      testCoverage={radarScores.test}
                      size={240}
                    />
                  </div>
                  
                  {/* 축별 점수 */}
                  <div className="grid grid-cols-3 gap-3 mt-4">
                    <div className="p-3 bg-terminal-dim/10 rounded-lg border border-terminal-border text-center">
                      <span className="text-lg">📋</span>
                      <div className="text-xs text-terminal-muted mt-1">{lang === 'ko' ? '기획' : 'Planning'}</div>
                      <div className={`text-xl font-bold ${radarScores.planning >= 80 ? 'text-status-go' : radarScores.planning >= 60 ? 'text-status-warning' : 'text-status-danger'}`}>
                        {radarScores.planning}
                      </div>
                    </div>
                    <div className="p-3 bg-terminal-dim/10 rounded-lg border border-terminal-border text-center">
                      <span className="text-lg">⚠️</span>
                      <div className="text-xs text-terminal-muted mt-1">{lang === 'ko' ? '리스크' : 'Risk'}</div>
                      <div className={`text-xl font-bold ${radarScores.risk >= 80 ? 'text-status-go' : radarScores.risk >= 60 ? 'text-status-warning' : 'text-status-danger'}`}>
                        {radarScores.risk}
                      </div>
                    </div>
                    <div className="p-3 bg-terminal-dim/10 rounded-lg border border-terminal-border text-center">
                      <span className="text-lg">✅</span>
                      <div className="text-xs text-terminal-muted mt-1">{lang === 'ko' ? '테스트' : 'Test'}</div>
                      <div className={`text-xl font-bold ${radarScores.test >= 80 ? 'text-status-go' : radarScores.test >= 60 ? 'text-status-warning' : 'text-status-danger'}`}>
                        {radarScores.test}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* 입력 데이터 섹션 */}
              {detail && (
                (detail.planning_intent?.length > 0 ||
                  detail.risks?.length > 0 ||
                  Object.keys(detail.qa_status || {}).length > 0 ||
                  (detail.github_context as unknown as GithubContextForReport | null)?.commits?.length)
              ) && (
                <div className="card p-4">
                  <div className="text-terminal-muted text-xs mb-3 uppercase tracking-wide">
                    # {t.result.inputData}
                  </div>
                  
                  <div className="space-y-4">
                    {/* GitHub 커밋 분석(선택) */}
                    <GitHubReportBlock
                      lang={lang}
                      githubCtx={(detail.github_context as unknown as GithubContextForReport | null) ?? null}
                      inputData={{
                        planning_intent: detail.planning_intent || [],
                        risks: detail.risks || [],
                        suggested_testcases: detail.suggested_testcases || [],
                      }}
                    />

                    {/* 첨부된 기획서 이미지 (MAX/Team 플랜 전용) */}
                    {isCurrentlyMaxOrTeam && (planningImages.length > 0 || isLoadingImages) && (
                      <div className="p-3 bg-terminal-dim/10 rounded-lg border border-terminal-border">
                        <div className="text-terminal-dim text-xs mb-2 flex items-center gap-2">
                          <span>🖼️</span>
                          <span>{lang === 'ko' ? '첨부된 기획 이미지' : 'Attached Planning Images'}</span>
                          <span className="px-1.5 py-0.5 bg-purple-500/30 text-purple-400 text-xs font-semibold rounded">MAX</span>
                        </div>
                        {isLoadingImages ? (
                          <div className="flex items-center justify-center py-4">
                            <div className="w-6 h-6 border-2 border-status-go/30 border-t-status-go rounded-full animate-spin"></div>
                            <span className="ml-2 text-sm text-terminal-muted">
                              {lang === 'ko' ? '이미지 로딩 중...' : 'Loading images...'}
                            </span>
                          </div>
                        ) : (
                          <div className="grid gap-3">
                            {planningImages.map((img, index) => (
                              <div key={index} className="relative group">
                                <div className="bg-terminal-dim/20 rounded-lg border border-terminal-border overflow-hidden">
                                  <div className="p-2 bg-terminal-dim/30 border-b border-terminal-border">
                                    <span className="text-xs text-terminal-muted truncate block">{img.name}</span>
                                  </div>
                                  <div className="p-2 flex justify-center">
                                    <img
                                      src={img.url}
                                      alt={img.name}
                                      className="max-w-full max-h-64 object-contain rounded cursor-pointer hover:opacity-90 transition-opacity"
                                      onClick={() => window.open(img.url, '_blank')}
                                      title={lang === 'ko' ? '클릭하여 크게 보기' : 'Click to view larger'}
                                    />
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* 기획 의도 */}
                    {detail.planning_intent && detail.planning_intent.length > 0 && (
                      <div>
                        <div className="text-terminal-dim text-xs mb-1">{t.form.planningIntent}</div>
                        <ul className="text-sm text-terminal-text space-y-1">
                          {detail.planning_intent.map((intent, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <span className="text-terminal-dim">•</span>
                              <span>{stripBullet(intent)}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* 리스크 */}
                    {detail.risks && detail.risks.length > 0 && (
                      <div>
                        <div className="text-terminal-dim text-xs mb-1">{t.form.risks}</div>
                        <ul className="text-sm text-terminal-text space-y-1">
                          {detail.risks.map((risk, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <span className="text-status-warning">⚠</span>
                              <span>{stripBullet(risk)}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* QA 상태 */}
                    {detail.qa_status && Object.keys(detail.qa_status).length > 0 && (
                      <div>
                        <div className="text-terminal-dim text-xs mb-1">{t.form.qaStatus}</div>
                        <div className="space-y-1">
                          {Object.entries(detail.qa_status).map(([key, value]) => (
                            <div key={key} className="flex items-center gap-2 text-sm">
                              <span className="text-terminal-muted">{key}:</span>
                              <span className={`${
                                value.includes('검증 완료') || value.includes('Verified') 
                                  ? 'text-status-go' 
                                  : value.includes('미검증') || value.includes('Unverified')
                                  ? 'text-status-danger'
                                  : 'text-status-warning'
                              }`}>
                                {value}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* AI 제안 테스트 케이스 */}
              {detail && detail.suggested_testcases && detail.suggested_testcases.length > 0 && (
                <SuggestedTestcasesResult testcases={detail.suggested_testcases} lang={lang} />
              )}

              {/* 상세 점수 */}
              <div className="card p-4">
                <div className="text-terminal-muted text-xs mb-3 uppercase tracking-wide">
                  # {t.result.itemScores}
                </div>
                
                <div className="space-y-4">
                  {result.breakdown.map((item) => {
                    const pct = Math.round((item.score / item.max_score) * 100);
                    const categoryLabel = lang === 'ko' ? item.category_ko : item.category;
                    return (
                      <div key={item.category}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-terminal-muted">{categoryLabel}</span>
                          <span className="text-terminal-text">
                            {item.score}<span className="text-terminal-dim">/{item.max_score}</span>
                            <span className="text-terminal-dim text-xs ml-2">({pct}%)</span>
                          </span>
                        </div>
                        <div className="progress-bar">
                          <div 
                            className={`progress-fill ${getBarColor(item.score, item.max_score)}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <div className="text-terminal-dim text-xs mt-1">{item.feedback}</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* 블로커 */}
              {result.blockers.length > 0 && (
                <div className="card p-4 border-status-danger">
                  <div className="text-status-danger text-xs mb-3 uppercase tracking-wide">
                    # {t.result.blockers} ({t.result.blockersSubtitle})
                  </div>
                  <ul className="space-y-2">
                    {result.blockers.map((blocker, i) => (
                      <li key={i} className="text-sm text-status-danger flex items-start gap-2">
                        <span className="text-terminal-dim">[{i + 1}]</span>
                        <span>{blocker}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* 구조화된 권고사항 */}
              {result.structured_recommendations && result.structured_recommendations.length > 0 ? (
                <div className="card p-4">
                  <div className="text-status-warning text-xs mb-4 uppercase tracking-wide">
                    # {t.result.recommendations}
                  </div>
                  <div className="space-y-4">
                    {result.structured_recommendations.map((rec: StructuredRecommendation, i: number) => {
                      const priorityConfig = getPriorityConfig(rec.priority);
                      return (
                        <div 
                          key={i} 
                          className={`border rounded-lg p-4 ${priorityConfig.bg}`}
                        >
                          {/* 헤더: 제목 + 우선순위 */}
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <h4 className={`font-semibold text-sm ${priorityConfig.color}`}>
                              {priorityConfig.icon} {rec.title}
                            </h4>
                            <span className={`text-xs px-2 py-0.5 rounded ${priorityConfig.color} border border-current/30`}>
                              {priorityConfig.label}
                            </span>
                          </div>
                          
                          {/* 설명 */}
                          <p className="text-terminal-muted text-sm mb-3">
                            {rec.description}
                          </p>
                          
                          {/* 액션 아이템 */}
                          {rec.action_items.length > 0 && (
                            <div className="mb-3">
                              <div className="text-terminal-dim text-xs mb-1 uppercase">
                                📋 {t.result.actionItems}
                              </div>
                              <ul className="space-y-1">
                                {rec.action_items.map((item, j) => (
                                  <li key={j} className="text-sm text-terminal-text flex items-start gap-2">
                                    <span className="text-terminal-dim">•</span>
                                    <span>{item}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {/* 메타 정보 */}
                          <div className="flex flex-wrap gap-4 text-xs text-terminal-dim pt-2 border-t border-terminal-border/50">
                            {rec.deadline && (
                              <div className="flex items-center gap-1">
                                <span>⏰</span>
                                <span className="text-terminal-muted">{t.result.deadline}:</span>
                                <span className="text-terminal-text">{rec.deadline}</span>
                              </div>
                            )}
                            {rec.owner && (
                              <div className="flex items-center gap-1">
                                <span>👤</span>
                                <span className="text-terminal-muted">{t.result.owner}:</span>
                                <span className="text-terminal-text">{rec.owner}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : result.recommendations.length > 0 && (
                <div className="card p-4">
                  <div className="text-status-warning text-xs mb-3 uppercase tracking-wide">
                    # {t.result.recommendations}
                  </div>
                  <ul className="space-y-2">
                    {result.recommendations.map((rec, i) => (
                      <li key={i} className="text-sm text-terminal-text flex items-start gap-2">
                        <span className="text-status-warning">→</span>
                        <span>{rec}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* 푸터 - 버튼들 */}
        {!isLoading && !error && detail && (
          <div className="flex-shrink-0 border-t border-terminal-border px-6 py-4 space-y-3">
            {/* PDF 에러 메시지 */}
            {pdfError && (
              <div className="bg-status-danger/10 border border-status-danger/30 text-status-danger text-sm p-2 rounded text-center">
                {pdfError}
              </div>
            )}
            
            {/* MAX/Team 플랜: AI 리포트 버튼 (MAX 또는 Team 플랜에서 생성된 평가) */}
            {user && (detail?.created_with_plan === 'max' || detail?.created_with_plan === 'team') && result && (
              (() => {
                const isCurrentlyMaxOrTeam = quotaInfo?.plan === 'max' || quotaInfo?.actual_plan === 'max' || quotaInfo?.plan === 'team' || quotaInfo?.team !== null;
                return isCurrentlyMaxOrTeam ? (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowMaxReport(true);
                    }}
                    className="w-full py-3 rounded-lg text-sm bg-gradient-to-r from-purple-500/20 to-blue-500/20 border border-purple-500/50 text-purple-400 hover:from-purple-500/30 hover:to-blue-500/30 transition-all flex items-center justify-center gap-2"
                  >
                    <span>✨</span>
                    {t.result.maxAiReport}
                    <span className="px-1.5 py-0.5 bg-purple-500/30 text-purple-300 text-xs font-semibold rounded">MAX</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      onOpenPricing?.();
                    }}
                    className="w-full py-3 rounded-lg text-sm bg-terminal-dim/30 border border-terminal-border text-terminal-muted hover:bg-terminal-dim/50 hover:border-purple-500/30 transition-all cursor-pointer flex flex-col items-center justify-center gap-1"
                  >
                    <div className="flex items-center gap-2">
                      <span>🔒</span>
                      <span>{t.result.maxAiReport}</span>
                      <span className="px-1.5 py-0.5 bg-purple-500/30 text-purple-400 text-xs font-semibold rounded">MAX</span>
                    </div>
                    <p className="text-xs text-terminal-muted/70">
                      {lang === 'ko' ? 'MAX 플랜으로 업그레이드하면 AI 리포트를 볼 수 있습니다' : 'Upgrade to MAX plan to view AI Report'}
                    </p>
                  </button>
                );
              })()
            )}
            
            {/* 첨부파일 버튼들 (2열 그리드) */}
            <div className="grid grid-cols-2 gap-3">
              {/* 첨부한 테스트케이스 버튼 (항상 표시, 없으면 비활성화) */}
              <button
                onClick={handleDownloadTestcase}
                disabled={!detail?.has_testcase_file || isDownloadingTestcase}
                className={`py-3 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                  detail?.has_testcase_file 
                    ? 'bg-terminal-bg border border-status-go text-status-go hover:bg-status-go/10' 
                    : 'bg-terminal-bg border border-terminal-border text-terminal-muted opacity-50 cursor-not-allowed'
                }`}
              >
                {isDownloadingTestcase ? (
                  <svg className="w-4 h-4 animate-spin flex-shrink-0" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                )}
                <span className="truncate">
                  {lang === 'ko' ? '첨부한 테스트케이스' : 'Attached Testcase'}
                </span>
              </button>

              {/* 첨부한 기획서 버튼 (MAX/Team 플랜 전용) */}
              {isCurrentlyMaxOrTeam ? (
                <button
                  onClick={() => {
                    if (!detail?.has_planning_files) return;
                    // 파일이 1개면 바로 다운로드, 여러 개면 모달 표시
                    if ((detail?.planning_file_names?.length || 0) === 1) {
                      handleDownloadPlanningFile(0);
                    } else {
                      setShowPlanningFilesModal(true);
                    }
                  }}
                  disabled={!detail?.has_planning_files || downloadingPlanningIndex !== null}
                  className={`py-3 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                    detail?.has_planning_files 
                      ? 'bg-terminal-bg border border-status-go text-status-go hover:bg-status-go/10' 
                      : 'bg-terminal-bg border border-terminal-border text-terminal-muted opacity-50 cursor-not-allowed'
                  }`}
                >
                  {downloadingPlanningIndex !== null ? (
                    <svg className="w-4 h-4 animate-spin flex-shrink-0" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  )}
                  <span className="truncate">
                    {lang === 'ko' ? '첨부한 기획서' : 'Attached Planning Doc'}
                    {(detail?.planning_file_names?.length || 0) > 1 && ` (${detail?.planning_file_names?.length})`}
                  </span>
                </button>
              ) : (
                <div
                  className="py-3 rounded-lg text-sm font-medium bg-terminal-bg border border-terminal-border text-terminal-muted opacity-60 cursor-not-allowed flex items-center justify-center gap-2"
                  title={lang === 'ko' ? 'MAX 플랜에서 사용 가능' : 'Available for MAX plan'}
                >
                  <span>🔒</span>
                  <span className="truncate">
                    {lang === 'ko' ? '첨부한 기획서' : 'Attached Planning Doc'}
                  </span>
                  <span className="px-1.5 py-0.5 bg-purple-500/30 text-purple-400 text-xs font-semibold rounded">MAX</span>
                </div>
              )}
            </div>

            {/* PDF 리포트 뷰어 버튼 (전체 너비) */}
            <button
              onClick={handleOpenPdfViewer}
              disabled={isPdfLoading}
              className="w-full py-3 rounded-lg text-sm font-medium border border-purple-500/50 text-purple-400 bg-purple-500/10 hover:bg-purple-500/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isPdfLoading ? (
                <svg className="w-4 h-4 animate-spin flex-shrink-0" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              ) : (
                <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              )}
              <span>
                {isPdfLoading 
                  ? (lang === 'ko' ? 'PDF 로딩 중...' : 'Loading PDF...') 
                  : (lang === 'ko' ? 'PDF 리포트 보기' : 'View PDF Report')}
              </span>
              {canDownloadPdf && (
                <span className="text-xs bg-purple-500/30 px-1.5 py-0.5 rounded">
                  {lang === 'ko' ? '다운로드 가능' : 'Download available'}
                </span>
              )}
            </button>

            {/* 다시 평가하기 버튼 (전체 너비) */}
            <button
              onClick={handleReEvaluate}
              className="w-full py-3 rounded-lg text-sm font-medium bg-status-go text-white hover:bg-status-go/90 transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {t.history.reEvaluate}
            </button>
          </div>
        )}
      </div>
      </div>
    </React.Fragment>
  );
}

